export type DatePreset = 'all' | 'today' | '7d' | '30d' | 'month' | 'custom'

export interface DateRangeValue {
  preset: DatePreset
  from: string | null
  to: string | null
}

export interface ResolvedDateRange {
  dateFrom?: string
  dateTo?: string
}

export interface CustomDateRange {
  from: string
  to: string
}

export const DATE_PRESET_LABELS: Record<DatePreset, string> = {
  all: 'Todo o período',
  today: 'Hoje',
  '7d': '7 dias',
  '30d': '30 dias',
  month: 'Este mês',
  custom: 'Personalizado',
}

export const DATE_PRESET_OPTIONS: DatePreset[] = ['today', '7d', '30d', 'month', 'all', 'custom']

export const DEFAULT_DATE_RANGE: DateRangeValue = { preset: '30d', from: null, to: null }

function startOfUtcDay(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()))
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * 86400000)
}

function parseLocalDate(value: string): Date | null {
  const parsed = new Date(`${value}T00:00:00Z`)
  return Number.isNaN(parsed.getTime()) ? null : startOfUtcDay(parsed)
}

export function getDefaultCustomDateRange(now: Date = new Date()): CustomDateRange {
  const today = startOfUtcDay(now)

  return {
    from: addDays(today, -29).toISOString().slice(0, 10),
    to: today.toISOString().slice(0, 10),
  }
}

export function resolveDateRange(value: DateRangeValue, now: Date = new Date()): ResolvedDateRange {
  const today = startOfUtcDay(now)

  switch (value.preset) {
    case 'all':
      return {}
    case 'today':
      return { dateFrom: today.toISOString(), dateTo: addDays(today, 1).toISOString() }
    case '7d':
      return { dateFrom: addDays(today, -6).toISOString(), dateTo: addDays(today, 1).toISOString() }
    case '30d':
      return {
        dateFrom: addDays(today, -29).toISOString(),
        dateTo: addDays(today, 1).toISOString(),
      }
    case 'month': {
      const first = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))
      const next = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1))
      return { dateFrom: first.toISOString(), dateTo: next.toISOString() }
    }
    case 'custom': {
      const resolved: ResolvedDateRange = {}
      const from = value.from ? parseLocalDate(value.from) : null
      const to = value.to ? parseLocalDate(value.to) : null
      if (from) {
        resolved.dateFrom = from.toISOString()
      }
      if (to) {
        resolved.dateTo = addDays(to, 1).toISOString()
      }
      return resolved
    }
  }
}

export function isDateRangeActive(value: DateRangeValue): boolean {
  if (value.preset === 'custom') {
    return Boolean(value.from || value.to)
  }
  return value.preset !== DEFAULT_DATE_RANGE.preset
}
